import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Download, FileText, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from './components/StatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return dateStr; }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
};

// Amendment List shown in Contract Detail
export const AmendmentsList = ({ contractId, contractNumber }) => {
  const [amendments, setAmendments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  const fetchAmendments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contracts/${contractId}/amendments`);
      setAmendments(res.data);
    } catch (error) {
      console.error('Failed to fetch amendments:', error);
    }
    finally { setLoading(false); }
  }, [contractId]);

  useEffect(() => { fetchAmendments(); }, [fetchAmendments]);

  if (loading) return <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--navy)' }} />;

  return (
    <div data-testid="amendments-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div className="detail-section-title" style={{ margin: 0 }}>Amendments / Addendums ({amendments.length})</div>
        <Button variant="outline" size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-amendment-btn"
          style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}>
          <Plus className="w-4 h-4 mr-1" /> New Amendment
        </Button>
      </div>

      {showForm && (
        <AmendmentForm 
          contractId={contractId} 
          contractNumber={contractNumber}
          onSaved={() => { setShowForm(false); fetchAmendments(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {amendments.length === 0 && !showForm ? (
        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', background: 'var(--gray-light)', borderRadius: 'var(--radius)' }}>
          No amendments yet
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {amendments.map((amd) => (
            <div key={amd.id} className="form-card" style={{ marginBottom: 0, cursor: 'pointer' }}
              onClick={() => navigate(`/amendments/${amd.id}`)} data-testid={`amendment-${amd.amendment_number}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px' }}>
                <div style={{ 
                  width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--navy)', color: '#fff', fontWeight: 600, fontSize: '13px', flexShrink: 0
                }}>
                  {amd.amendment_number}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: '14px' }}>{amd.amendment_ref}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {amd.description?.substring(0, 80)}{amd.description?.length > 80 ? '...' : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Effective: {formatDate(amd.effective_date)}</div>
                  <StatusBadge status={amd.status} />
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-light)' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Amendment Create/Edit Form
const AmendmentForm = ({ contractId, contractNumber, onSaved, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    effective_date: '',
    description: '',
    reason: '',
    revised_monthly_value: '',
    revised_end_date: '',
    changes: [{ field: '', old_value: '', new_value: '' }],
  });

  const updateChange = (index, field, value) => {
    const changes = [...form.changes];
    changes[index] = { ...changes[index], [field]: value };
    setForm(prev => ({ ...prev, changes }));
  };

  const addChange = () => {
    setForm(prev => ({ ...prev, changes: [...prev.changes, { field: '', old_value: '', new_value: '' }] }));
  };

  const removeChange = (index) => {
    setForm(prev => ({ ...prev, changes: prev.changes.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (!form.effective_date || !form.description) {
      toast.error("Please fill in effective date and description");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        revised_monthly_value: form.revised_monthly_value ? parseFloat(form.revised_monthly_value) : null,
        changes: form.changes.filter(c => c.field),
      };
      await axios.post(`${API}/contracts/${contractId}/amendments`, payload);
      toast.success("Amendment created successfully");
      onSaved();
    } catch (error) {
      toast.error("Failed to create amendment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="form-card" style={{ marginBottom: '16px', border: '2px solid var(--navy)' }} data-testid="amendment-form">
      <div style={{ padding: '20px' }}>
        <div style={{ fontWeight: 600, marginBottom: '16px', color: 'var(--navy)' }}>New Amendment / Addendum</div>
        
        <div className="form-grid-2">
          <div>
            <Label>Effective Date *</Label>
            <Input type="date" value={form.effective_date} onChange={e => setForm(prev => ({ ...prev, effective_date: e.target.value }))}
              data-testid="amendment-effective-date" />
          </div>
          <div>
            <Label>Reason for Amendment</Label>
            <Input value={form.reason} onChange={e => setForm(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="e.g., Rate revision, Scope change" data-testid="amendment-reason" />
          </div>
        </div>

        <div style={{ marginTop: '12px' }}>
          <Label>Description of Changes *</Label>
          <Textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
            rows={3} placeholder="Describe the changes being made to the original contract..."
            data-testid="amendment-description" />
        </div>

        <div style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <Label style={{ margin: 0 }}>Specific Changes</Label>
            <Button variant="ghost" size="sm" onClick={addChange}>
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
          {form.changes.map((change, index) => (
            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <Input value={change.field} onChange={e => updateChange(index, 'field', e.target.value)}
                placeholder="Field name" style={{ flex: 1 }} />
              <Input value={change.old_value} onChange={e => updateChange(index, 'old_value', e.target.value)}
                placeholder="Old value" style={{ flex: 1 }} />
              <Input value={change.new_value} onChange={e => updateChange(index, 'new_value', e.target.value)}
                placeholder="New value" style={{ flex: 1 }} />
              <button className="icon-btn" onClick={() => removeChange(index)} style={{ color: 'var(--red)' }}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="form-grid-2" style={{ marginTop: '12px' }}>
          <div>
            <Label>Revised Monthly Value (optional)</Label>
            <Input type="number" value={form.revised_monthly_value}
              onChange={e => setForm(prev => ({ ...prev, revised_monthly_value: e.target.value }))}
              placeholder="New monthly value" data-testid="amendment-revised-value" />
          </div>
          <div>
            <Label>Revised End Date (optional)</Label>
            <Input type="date" value={form.revised_end_date}
              onChange={e => setForm(prev => ({ ...prev, revised_end_date: e.target.value }))} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} style={{ background: 'var(--navy)', color: '#fff' }}
            data-testid="save-amendment-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Create Amendment
          </Button>
        </div>
      </div>
    </div>
  );
};

// Amendment Detail Page
export const AmendmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [amendment, setAmendment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/amendments/${id}`);
        setAmendment(res.data);
      } catch { toast.error("Failed to load amendment"); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const handleExport = async () => {
    try {
      toast.info("Generating amendment document...");
      const response = await window.fetch(`${API}/amendments/${id}/export/word`);
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const filename = `${amendment.amendment_ref.replace(/\//g, '_')}.docx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      toast.success(`Downloaded ${filename}`);
    } catch { toast.error("Failed to export amendment"); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this amendment?")) return;
    try {
      await axios.delete(`${API}/amendments/${id}`);
      toast.success("Amendment deleted");
      navigate(-1);
    } catch { toast.error("Failed to delete amendment"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!amendment) return null;

  return (
    <div className="animate-fadeIn" data-testid="amendment-detail-page">
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link>
        <span>&rsaquo;</span>
        <Link to="/contracts">Contracts</Link>
        <span>&rsaquo;</span>
        <Link to={`/contracts/${amendment.contract_id}`}>{amendment.contract_number}</Link>
        <span>&rsaquo;</span>
        <span>Amendment {amendment.amendment_number}</span>
      </div>

      <div className="detail-card">
        <div className="detail-header-band">
          <div className="dh-left">
            <div className="contract-num-lg">{amendment.amendment_ref}</div>
            <div className="type-lg">Amendment / Addendum No. {amendment.amendment_number}</div>
          </div>
          <div className="dh-right">
            <StatusBadge status={amendment.status} style={{ fontSize: '13px', padding: '5px 14px' }} testId="amendment-status-badge" />
          </div>
        </div>

        <div className="detail-body-inner">
          <div className="info-grid">
            <div className="info-row"><div className="info-k">Parent Contract</div><div className="info-v mono">{amendment.contract_number}</div></div>
            <div className="info-row"><div className="info-k">Effective Date</div><div className="info-v mono">{formatDate(amendment.effective_date)}</div></div>
            <div className="info-row"><div className="info-k">Reason</div><div className="info-v">{amendment.reason || 'N/A'}</div></div>
            <div className="info-row"><div className="info-k">Created</div><div className="info-v mono">{formatDate(amendment.created_at)}</div></div>
          </div>

          <div className="detail-section-title">Description</div>
          <div style={{ padding: '16px 20px', background: 'var(--gray-light)', borderRadius: 'var(--radius)', whiteSpace: 'pre-wrap' }}>
            {amendment.description || 'N/A'}
          </div>

          {amendment.changes && amendment.changes.length > 0 && (
            <>
              <div className="detail-section-title">Changes</div>
              <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                <table className="contract-table" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Field</th>
                      <th>Old Value</th>
                      <th>New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amendment.changes.map((change, idx) => (
                      <tr key={idx} style={{ cursor: 'default' }}>
                        <td>{idx + 1}</td>
                        <td style={{ fontWeight: 500 }}>{change.field}</td>
                        <td style={{ color: 'var(--red)' }}>{change.old_value}</td>
                        <td style={{ color: 'var(--green)' }}>{change.new_value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {amendment.revised_monthly_value && (
            <div className="fin-callout" style={{ marginTop: '16px' }}>
              <div className="fin-item">
                <div className="fl">Revised Monthly Value</div>
                <div className="fv total">{formatCurrency(amendment.revised_monthly_value)}</div>
              </div>
            </div>
          )}

          {amendment.revised_end_date && (
            <div style={{ marginTop: '12px', padding: '12px 16px', background: 'var(--gold-pale)', border: '1px solid var(--gold)', borderRadius: 'var(--radius)' }}>
              <span style={{ fontWeight: 500, color: 'var(--amber)' }}>Revised End Date: </span>
              <span style={{ fontFamily: "'DM Mono', monospace" }}>{formatDate(amendment.revised_end_date)}</span>
            </div>
          )}
        </div>

        <div className="detail-footer">
          <button className="btn btn-ghost" onClick={() => navigate(`/contracts/${amendment.contract_id}`)}>
            &larr; Back to Contract
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
            <button className="btn btn-gold" onClick={handleExport} data-testid="export-amendment-btn">
              <Download className="w-4 h-4" style={{ marginRight: '6px', display: 'inline' }} />
              Export as Word
            </button>
            <button className="btn btn-danger" onClick={handleDelete} data-testid="delete-amendment-btn">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
